import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import type { Settings, User } from '@api/api';
import { endpoints, qk } from '../api/endpoints';
import { EmptyState, ErrorState, OneTimeSecret, PageHeader, RelTime, SectionCard, SkeletonRows } from '../components/common';
import { LoadingButton } from '../components/LoadingButton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useNotify } from '../components/Notify';
import { useAuth } from '../auth/AuthProvider';
import { MONO } from '../theme';

const MASK = '••••';

// ------------------------------------------------------------------ general

function SecretSetting({ label, value, onChange, help }: { label: string; value: string | null; onChange: (v: string | null) => void; help: string }) {
  const isMasked = value === MASK;
  return (
    <TextField
      fullWidth
      label={label}
      type="password"
      value={isMasked ? '' : value ?? ''}
      placeholder={isMasked ? '•••• set — type to replace' : undefined}
      InputLabelProps={isMasked ? { shrink: true } : undefined}
      onChange={(e) => onChange(e.target.value)}
      helperText={help}
      autoComplete="off"
    />
  );
}

function GeneralTab() {
  const qc = useQueryClient();
  const notify = useNotify();
  const q = useQuery({ queryKey: qk.settings, queryFn: endpoints.settings });
  const releases = useQuery({ queryKey: qk.releases, queryFn: endpoints.releases });
  const [s, setS] = useState<Settings | null>(null);
  useEffect(() => {
    if (q.data) setS(q.data);
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => {
      const body: Partial<Settings> = { ...s! };
      // unchanged secrets stay masked: don't send them
      if (body.telegramBotToken === MASK) delete body.telegramBotToken;
      if (body.githubToken === MASK) delete body.githubToken;
      return endpoints.putSettings(body);
    },
    onSuccess: (d) => (qc.setQueryData(qk.settings, d), notify.ok('Settings saved')),
    onError: (e) => notify.error(e),
  });
  const test = useMutation({ mutationFn: endpoints.testTelegram, onSuccess: () => notify.ok('Test message sent — check Telegram'), onError: (e) => notify.error(e, 'Telegram') });

  if (q.isLoading || !s) return q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <SkeletonRows rows={5} height={48} />;
  const set = (p: Partial<Settings>) => setS({ ...s, ...p });

  return (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      <SectionCard
        title="Telegram alerts"
        action={
          <LoadingButton size="small" startIcon={<SendOutlinedIcon />} loading={test.isPending} onClick={() => test.mutate()}>
            Send test
          </LoadingButton>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={7}>
            <SecretSetting label="Bot token" value={s.telegramBotToken} onChange={(v) => set({ telegramBotToken: v })} help="From @BotFather" />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth label="Chat ID" value={s.telegramChatId ?? ''} onChange={(e) => set({ telegramChatId: e.target.value })} helperText="Group or channel, e.g. -100…" />
          </Grid>
        </Grid>
      </SectionCard>
      <SectionCard title="GitHub">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SecretSetting label="Token" value={s.githubToken} onChange={(v) => set({ githubToken: v })} help="Fine-grained token with read access to both repos" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Backend repo" value={s.backendRepo} onChange={(e) => set({ backendRepo: e.target.value })} helperText="owner/name" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="CRM repo" value={s.crmRepo} onChange={(e) => set({ crmRepo: e.target.value })} helperText="owner/name" />
          </Grid>
        </Grid>
      </SectionCard>
      <SectionCard title="Defaults">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Base domain" value={s.baseDomain} onChange={(e) => set({ baseDomain: e.target.value.trim() })} helperText="New clones get <slug>.<base>" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Server IP" value={s.serverIp} onChange={(e) => set({ serverIp: e.target.value.trim() })} helperText="Custom domains must point here" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="Default release" value={s.defaultReleaseId ?? ''} onChange={(e) => set({ defaultReleaseId: e.target.value === '' ? null : Number(e.target.value) })}>
              <MenuItem value="">—</MenuItem>
              {(releases.data ?? []).map((r) => (
                <MenuItem key={r.id} value={r.id} disabled={r.status !== 'ready'}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </SectionCard>
      <Box>
        <LoadingButton variant="contained" loading={save.isPending} onClick={() => save.mutate()}>
          Save settings
        </LoadingButton>
      </Box>
    </Stack>
  );
}

// ------------------------------------------------------------------ users

function UsersTab() {
  const qc = useQueryClient();
  const notify = useNotify();
  const me = useAuth().user;
  const q = useQuery({ queryKey: qk.users, queryFn: endpoints.users });
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<User['role']>('viewer');
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);
  const [toDelete, setToDelete] = useState<User | null>(null);
  const [reset, setReset] = useState<{ user: User; kind: 'password' | 'totp' } | null>(null);
  const [resetPw, setResetPw] = useState<{ email: string; password: string } | null>(null);
  const doReset = useMutation({
    mutationFn: (r: { user: User; kind: 'password' | 'totp' }) => endpoints.patchUser(r.user.id, r.kind === 'password' ? { resetPassword: true } : { resetTotp: true }),
    onSuccess: (res, r) => {
      void qc.invalidateQueries({ queryKey: qk.users });
      setReset(null);
      if (res.password) setResetPw({ email: res.user.email, password: res.password });
      else notify.ok(r.kind === 'totp' ? `2FA reset — ${r.user.email} enrols again at next sign-in` : 'Password reset');
    },
    onError: (e) => notify.error(e),
  });
  const create = useMutation({
    mutationFn: () => endpoints.createUser({ email: email.trim(), role }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: qk.users });
      setCreated({ email: r.user.email, password: r.password });
      setEmail('');
    },
    onError: (e) => notify.error(e),
  });
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { role?: User['role']; disabled?: boolean } }) => endpoints.patchUser(id, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.users }),
    onError: (e) => notify.error(e),
  });
  const del = useMutation({
    mutationFn: (id: number) => endpoints.deleteUser(id),
    onSuccess: () => (void qc.invalidateQueries({ queryKey: qk.users }), setToDelete(null)),
    onError: (e) => notify.error(e),
  });
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <Stack spacing={2}>
      <Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => (setCreated(null), setOpen(true))}>
          Add user
        </Button>
      </Box>
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.isLoading ? (
        <SkeletonRows rows={3} />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>E-mail</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>2FA</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell>Active</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(q.data ?? []).map((u) => {
                const self = u.id === me?.id;
                return (
                  <TableRow key={u.id} sx={{ opacity: u.disabled ? 0.6 : 1 }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {u.email} {self && <Chip size="small" label="you" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>
                      <TextField select variant="standard" value={u.role} disabled={self} onChange={(e) => patch.mutate({ id: u.id, body: { role: e.target.value as User['role'] } })} InputProps={{ disableUnderline: true }} inputProps={{ 'aria-label': `Role of ${u.email}` }}>
                        <MenuItem value="owner">Owner</MenuItem>
                        <MenuItem value="viewer">Viewer</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>{u.totpEnabled ? <Chip size="small" color="success" variant="outlined" label="on" /> : <Chip size="small" label="not enrolled" />}</TableCell>
                    <TableCell>
                      <RelTime iso={u.lastLoginAt} />
                    </TableCell>
                    <TableCell>
                      <Switch size="small" checked={!u.disabled} disabled={self} onChange={(e) => patch.mutate({ id: u.id, body: { disabled: !e.target.checked } })} inputProps={{ 'aria-label': `Active ${u.email}` }} />
                    </TableCell>
                    <TableCell align="right">
                      {!self && (
                        <>
                          <Button size="small" onClick={() => setReset({ user: u, kind: 'password' })}>
                            Reset password
                          </Button>
                          {u.totpEnabled && (
                            <Button size="small" onClick={() => setReset({ user: u, kind: 'totp' })}>
                              Reset 2FA
                            </Button>
                          )}
                        </>
                      )}
                      {!self && (
                        <Tooltip title="Delete user">
                          <IconButton size="small" onClick={() => setToDelete(u)} aria-label={`Delete ${u.email}`}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary">
        Owners can change everything; viewers see the dashboards and logs but cannot run actions or read secrets.
      </Typography>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{created ? 'User created' : 'Add user'}</DialogTitle>
        <DialogContent>
          {created ? (
            <OneTimeSecret title={`Password for ${created.email}`} value={created.password}>
              <Typography variant="body2">They set up 2FA on first sign-in.</Typography>
            </OneTimeSecret>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
              <TextField select label="Role" value={role} onChange={(e) => setRole(e.target.value as User['role'])}>
                <MenuItem value="viewer">Viewer — read only</MenuItem>
                <MenuItem value="owner">Owner — full access</MenuItem>
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {created ? (
            <Button variant="contained" onClick={() => setOpen(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <LoadingButton variant="contained" disabled={!emailOk} loading={create.isPending} onClick={() => create.mutate()}>
                Create
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!reset}
        title={reset?.kind === 'password' ? `Reset the password of ${reset?.user.email}?` : `Reset 2FA of ${reset?.user.email}?`}
        text={reset?.kind === 'password' ? 'A new password is generated and shown once; their sessions end.' : 'They will set up the authenticator again at the next sign-in.'}
        confirmLabel="Reset"
        danger
        busy={doReset.isPending}
        onClose={() => setReset(null)}
        onConfirm={() => reset && doReset.mutate(reset)}
      />
      <Dialog open={!!resetPw} onClose={() => setResetPw(null)} maxWidth="sm" fullWidth>
        <DialogTitle>New password</DialogTitle>
        <DialogContent>{resetPw && <OneTimeSecret title={`Password for ${resetPw.email}`} value={resetPw.password} />}</DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setResetPw(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={!!toDelete} title={`Delete ${toDelete?.email}?`} text="Their sessions end immediately." confirmLabel="Delete" danger busy={del.isPending} onClose={() => setToDelete(null)} onConfirm={() => toDelete && del.mutate(toDelete.id)} />
    </Stack>
  );
}

// ------------------------------------------------------------------ tokens

export function TokensTab() {
  const qc = useQueryClient();
  const notify = useNotify();
  const q = useQuery({ queryKey: qk.tokens, queryFn: endpoints.tokens });
  const [name, setName] = useState('');
  const [created, setCreated] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => endpoints.createToken(name.trim()),
    onSuccess: (t) => (void qc.invalidateQueries({ queryKey: qk.tokens }), setCreated(t.token), setName('')),
    onError: (e) => notify.error(e),
  });
  const del = useMutation({
    mutationFn: (id: number) => endpoints.deleteToken(id),
    onSuccess: () => (void qc.invalidateQueries({ queryKey: qk.tokens }), notify.ok('Token revoked')),
    onError: (e) => notify.error(e),
  });
  return (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      <Typography variant="body2" color="text.secondary">
        Personal tokens for tools/build-app.sh on the Mac. They can download brand packages.
      </Typography>
      <Stack direction="row" spacing={1} component="form" onSubmit={(e) => (e.preventDefault(), name.trim() && create.mutate())}>
        <TextField label="Token name" placeholder="MacBook build-app" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 1, maxWidth: 360 }} />
        <LoadingButton type="submit" variant="contained" disabled={!name.trim()} loading={create.isPending}>
          Create
        </LoadingButton>
      </Stack>
      {created && <OneTimeSecret title="New API token" value={created} />}
      {q.isError && <ErrorState error={q.error} />}
      {q.isLoading ? (
        <SkeletonRows rows={2} />
      ) : !q.data?.length ? (
        <EmptyState title="No tokens" />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {q.data.map((t) => (
                <TableRow key={t.id}>
                  <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                  <TableCell>
                    <RelTime iso={t.createdAt} />
                  </TableCell>
                  <TableCell>{t.lastUsedAt ? <RelTime iso={t.lastUsedAt} /> : 'never'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" onClick={() => del.mutate(t.id)}>
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

// ------------------------------------------------------------------ audit

function AuditTab() {
  const q = useQuery({ queryKey: qk.audit, queryFn: () => endpoints.audit(200) });
  const [filter, setFilter] = useState('');
  if (q.isLoading) return <SkeletonRows rows={6} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const f = filter.toLowerCase();
  const rows = (q.data ?? []).filter((a) => !f || `${a.action} ${a.target ?? ''} ${a.user ?? ''}`.toLowerCase().includes(f));
  return (
    <Stack spacing={2}>
      <TextField placeholder="Filter by action, target or user" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ maxWidth: 400 }} inputProps={{ 'aria-label': 'Filter audit log' }} />
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <RelTime iso={a.ts} />
                </TableCell>
                <TableCell>{a.user ?? 'system'}</TableCell>
                <TableCell sx={{ fontFamily: MONO, fontSize: 12.5 }}>{a.action}</TableCell>
                <TableCell>{a.target ?? '—'}</TableCell>
                <TableCell sx={{ fontFamily: MONO, fontSize: 12, color: 'text.secondary', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }} title={a.details ? JSON.stringify(a.details) : undefined}>
                  {a.details ? JSON.stringify(a.details) : ''}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{a.ip}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!rows.length && <EmptyState title="Nothing in the audit log" />}
      </TableContainer>
    </Stack>
  );
}

const TABS = [
  ['general', 'General'],
  ['users', 'Users'],
  ['audit', 'Audit log'],
] as const;

export function SettingsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const tab = TABS.find(([k]) => k === params.tab)?.[0] ?? 'general';
  return (
    <>
      <PageHeader title="Settings" subtitle="Panel-wide settings, people and tokens" />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => navigate(`/settings${v === 'general' ? '' : `/${v}`}`)} variant="scrollable" allowScrollButtonsMobile>
          {TABS.map(([k, l]) => (
            <Tab key={k} value={k} label={l} />
          ))}
        </Tabs>
      </Box>
      {tab === 'general' && <GeneralTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'audit' && <AuditTab />}
    </>
  );
}
